import { useCallback, useEffect, useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { DASHBOARD_KEY } from '../../constants/kpi.constants';
import { CacDetailsResponseDto, CacResponseDto, KpiDashboardMetaResponseDto } from '../../types/response';
import KpiCacCardComponent from './kpi-cac-card';

interface KpiCacCardProps {
  meta: KpiDashboardMetaResponseDto;
  className?: string;
}

function buildCacQuery(fromMonth: number, fromYear: number, toMonth: number, toYear: number): string {
  const params = new URLSearchParams();
  params.set('from[month]', String(fromMonth));
  params.set('from[year]', String(fromYear));
  params.set('to[month]', String(toMonth));
  params.set('to[year]', String(toYear));
  return `?${params.toString()}`;
}

const KpiCacCard = ({ meta, className }: KpiCacCardProps) => {
  const { apiRequest } = useApiRequest();

  const [fromYear, setFromYear] = useState(meta.currentYear - 1);
  const [fromMonth, setFromMonth] = useState(meta.currentMonth);
  const [toYear, setToYear] = useState(meta.currentYear);
  const [toMonth, setToMonth] = useState(meta.currentMonth);

  const [cac, setCac] = useState<CacResponseDto | null>(null);
  const [cacDetails, setCacDetails] = useState<CacDetailsResponseDto | null>(null);
  const [isCacLoading, setIsCacLoading] = useState(true);
  const [isCacDetailsLoading, setIsCacDetailsLoading] = useState(true);

  const fetchCac = useCallback(
    async (fMonth: number, fYear: number, tMonth: number, tYear: number) => {
      setIsCacLoading(true);
      try {
        const query = buildCacQuery(fMonth, fYear, tMonth, tYear);
        const response = await apiRequest<CacResponseDto>(`kpi-dashboards/cac/${DASHBOARD_KEY}${query}`, 'GET');
        if (response.data) setCac(response.data);
      } catch (error) {
        console.error('Error fetching CAC:', error);
      } finally {
        setIsCacLoading(false);
      }
    },
    [apiRequest]
  );

  const fetchCacDetails = useCallback(
    async (fMonth: number, fYear: number, tMonth: number, tYear: number) => {
      setIsCacDetailsLoading(true);
      try {
        const query = buildCacQuery(fMonth, fYear, tMonth, tYear);
        const response = await apiRequest<CacDetailsResponseDto>(`kpi-dashboards/cac/${DASHBOARD_KEY}/details${query}`, 'GET');
        if (response.data) setCacDetails(response.data);
      } catch (error) {
        console.error('Error fetching CAC details:', error);
      } finally {
        setIsCacDetailsLoading(false);
      }
    },
    [apiRequest]
  );

  useEffect(() => {
    Promise.all([fetchCac(fromMonth, fromYear, toMonth, toYear), fetchCacDetails(fromMonth, fromYear, toMonth, toYear)]);
  }, [fromMonth, fromYear, toMonth, toYear, fetchCac, fetchCacDetails]);

  const handleRangeChange = (range: { from: { month: string; year: string }; to: { month: string; year: string } }) => {
    const fMonth = Number(range.from.month);
    const fYear = Number(range.from.year);
    const tMonth = Number(range.to.month);
    const tYear = Number(range.to.year);

    setFromMonth(fMonth);
    setFromYear(fYear);
    setToMonth(tMonth);
    setToYear(tYear);

    Promise.all([fetchCac(fMonth, fYear, tMonth, tYear), fetchCacDetails(fMonth, fYear, tMonth, tYear)]);
  };

  return (
    <KpiCacCardComponent
      years={meta.years}
      range={{ fromYear, fromMonth, toYear, toMonth }}
      cac={cac}
      cacDetails={cacDetails}
      isCacLoading={isCacLoading}
      isCacDetailsLoading={isCacDetailsLoading}
      onRangeChange={handleRangeChange}
      className={className}
    />
  );
};

export default KpiCacCard;
